import { useState, useEffect, lazy, Suspense, useRef } from 'react';
import Hero from '../../components/home/Hero';
import LoyaltySection from '../../components/home/LoyaltySection';
const LocationsSection = lazy(() => import('../../components/home/LocationsSection'));
import AboutSection from '../../components/home/AboutSection';
import MenuCategoriesSlider from '../../components/shared/MenuCategoriesSlider';
import SEO from '../../components/shared/SEO';
import StructuredData from '../../components/shared/StructuredData';
import { getMenuCategories } from '../../lib/sanity';
import { generateOrganizationSchema, generateWebSiteSchema } from '../../utils/schema';

export default function HomePage() {
  const [menuCategories, setMenuCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shouldLoadLocations, setShouldLoadLocations] = useState(false);
  const locationsSectionRef = useRef(null);

  // Fetch menu categories from Sanity
  useEffect(() => {
    async function fetchCategories() {
      try {
        console.log('Fetching menu categories...');
        const categories = await getMenuCategories();
        console.log('Categories in homepage:', categories);
        
        if (categories && categories.length > 0) {
          setMenuCategories(categories);
        } else {
          console.warn('No menu categories returned from Sanity');
        }
      } catch (error) {
        console.error('Error in homepage when fetching menu categories:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchCategories();
  }, []);

  // Use Intersection Observer to only load LocationsSection when it's near viewport
  useEffect(() => {
    const currentRef = locationsSectionRef.current;
    if (!currentRef) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !shouldLoadLocations) {
            setShouldLoadLocations(true);
          }
        });
      },
      {
        rootMargin: '400px', // Start loading 400px before section enters viewport
        threshold: 0
      }
    );

    observer.observe(currentRef);

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [shouldLoadLocations]);

  return (
    <>
      <SEO 
        title="The Original Pancake House - Breakfast & Brunch Restaurant" 
        description="Welcome to The Original Pancake House. Serving made-from-scratch breakfast and brunch since 1953. Experience fresh ingredients, handcrafted recipes, and warm hospitality at our locations across the US."
        openGraph={{
          image: "/oph-social-share1.jpg"
        }}
      />
      
      {/* Structured Data for SEO */}
      <StructuredData data={[
        generateOrganizationSchema(),
        generateWebSiteSchema()
      ]} />
      
      <div>
        <Hero />
        <MenuCategoriesSlider categories={menuCategories} loading={loading} />
        <LoyaltySection />
        
        {/* Sentinel element for Intersection Observer */}
        <div ref={locationsSectionRef}>
          {shouldLoadLocations ? (
            <Suspense fallback={
              <div className="pt-0 md:pt-16 pb-16 bg-white flex items-center justify-center" style={{ minHeight: '600px' }}>
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-navy"></div>
              </div>
            }>
              <LocationsSection />
            </Suspense>
          ) : (
            <div className="pt-0 md:pt-16 pb-16 bg-white" style={{ minHeight: '600px' }} />
          )}
        </div>
        
        <AboutSection />
      </div>
    </>
  );
}